import { ROUTER_CONFIG_SNAPSHOT } from '@dupe/common/affiliateRouter'
import {
  applyRouterSearchUrlTemplate,
  sanitizeRouterSearchQuery,
} from '@dupe/common/routerDestination'
import kebabCase from 'lodash/kebabCase'

// Re-exported because this module was the query-sanitizing home before the server
// needed the same function. There is now exactly ONE implementation, in
// `@dupe/common/routerDestination`, because the client and the server have to
// build byte-identical URLs during step 5's cutover — two copies of a regex that
// strips characters from a search term is precisely the drift that ends with two
// shoppers landing on different pages.
export { sanitizeRouterSearchQuery }

const ROUTER_SEARCH_URL_TEMPLATES: Record<string, string> = {}
// Merchants whose search endpoint rejects cold/cookieless traffic land on a
// fixed URL instead of the query search (DUP-1630). See RouterMerchant.landingUrl.
const ROUTER_LANDING_URLS: Record<string, string> = {}
// From the snapshot rather than `ROUTER_CONFIG`, so retired partners keep their
// URL builder: `ROUTER_CONFIG` drops them from routing, but a link or decision
// that already names one must still build.
for (const entry of Object.values(ROUTER_CONFIG_SNAPSHOT.lanes).flat()) {
  ROUTER_SEARCH_URL_TEMPLATES[entry.shopKey] ??= entry.searchUrlTemplate
  if (entry.landingUrl) {
    ROUTER_LANDING_URLS[entry.shopKey] ??= entry.landingUrl
  }
}

const LEGACY_SHOP_SEARCH_URL_BUILDERS: Record<
  string,
  (query: string) => string
> = {
  amazon: (query) => `https://www.amazon.com/s?k=${query}`,
  bbb: (query) =>
    `https://www.bedbathandbeyond.com/${kebabCase(query)},/k,/results.html?SearchType=Header`,
}

/**
 * Get the direct shop search URL without redirect wrapper
 */
export const getShopSearchUrl = ({
  query,
  shopKey,
}: {
  query: string
  shopKey: string
}) => {
  const landingUrl = ROUTER_LANDING_URLS[shopKey]
  if (landingUrl) return landingUrl

  const routerTemplate = ROUTER_SEARCH_URL_TEMPLATES[shopKey]
  if (routerTemplate) return applyRouterSearchUrlTemplate(routerTemplate, query)

  return LEGACY_SHOP_SEARCH_URL_BUILDERS[shopKey]?.(query)
}

/**
 * Get the search URL wrapped with redirect tracking
 */
const getSearchUrl = ({
  query,
  source,
  shopKey,
  hash,
}: {
  query: string
  shopKey: string
  source?: string
  hash?: string | null
}) => {
  const shopUrl = getShopSearchUrl({ query, shopKey })

  if (!shopUrl) return undefined

  // `link` is interpolated unencoded, so the retailer URL's own query string
  // splits into top-level params. `hash` is escaped and would still parse from
  // either position, but if a retailer URL ever carries its own `hash=`, the
  // server's `searchParams.get('hash')` takes the first occurrence — so ours
  // has to precede `link` to win.
  return `/redirect?s=1${source ? `&src=input-${source}` : ''}${
    hash ? `&hash=${encodeURIComponent(hash)}` : ''
  }&link=${shopUrl}`
}

export default getSearchUrl
